import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { Paginated, Payable } from "../types/dtos";

export type PayableListParams = { page?: number; pageSize?: number; q?: string; status?: string };

async function fetchPayables(params: PayableListParams): Promise<Paginated<Payable>> {
  const res = await staffApi.get("/payables", { params });
  return res.data;
}

export function usePayables(params: PayableListParams): UseQueryResult<Paginated<Payable>> {
  return useQuery({
    queryKey: ["payables", params],
    queryFn: () => fetchPayables(params),
    placeholderData: (prev: Paginated<Payable> | undefined) => prev,
  });
}

export type PayableFormInput = {
  supplierName: string;
  category: string;
  dueDate: string;
  value: number;
  notes?: string;
};

export function useCreatePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PayableFormInput) => staffApi.post("/payables", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payables"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useMarkPayablePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, settlementTypeId, paidAt }: { id: number; settlementTypeId: number; paidAt: string }) =>
      staffApi.post(`/payables/${id}/pay`, { settlementTypeId, paidAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payables"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useDeletePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => staffApi.delete(`/payables/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payables"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
